import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import type { UseProductDataProps, ProductDataResult } from './product-data/types';
import { DEFAULT_PRODUCT_STATE } from './product-data/defaultState';
import { fetchProductData } from './product-data/fetchProduct';

export const useProductData = ({ 
  productId, 
  isNewProduct = false, 
  onBack, 
  shopId,
  organizationId: propOrganizationId
}: UseProductDataProps): ProductDataResult => {
  const [loading, setLoading] = useState(!isNewProduct);
  const [productState, setProductState] = useState<typeof DEFAULT_PRODUCT_STATE>({
    ...DEFAULT_PRODUCT_STATE,
    shopId: shopId || '',
    organizationId: propOrganizationId || null
  });
  
  const { toast } = useToast();

  useEffect(() => {
    // If creating a new product, skip fetching
    if (isNewProduct) {
      console.log("Creating new product - using default state with shopId:", shopId, "and organizationId:", propOrganizationId);
      setProductState(prev => ({
        ...prev,
        shopId: shopId || '',
        organizationId: propOrganizationId || null
      }));
      setLoading(false);
      return;
    }

    // Otherwise fetch existing product
    const loadProduct = async () => {
      setLoading(true);
      
      const fetchedProductState = await fetchProductData(productId, toast, onBack);
      
      if (fetchedProductState) {
        // If shopId was explicitly provided as a prop, prefer it over the one from the product
        const finalShopId = shopId || fetchedProductState.shopId;
        const finalOrganizationId = propOrganizationId || fetchedProductState.organizationId;
        
        setProductState({
          ...fetchedProductState,
          shopId: finalShopId,
          organizationId: finalOrganizationId
        });
        
        console.log("Media URLs loaded:", fetchedProductState.mediaUrls);
        console.log("Organization ID set:", finalOrganizationId);
      }
      
      setLoading(false);
    };

    loadProduct();
  }, [productId, isNewProduct, onBack, toast, shopId, propOrganizationId]);

  return {
    loading,
    productState,
    setProductState
  };
};

// Export the product data types but not the hook again
export { DEFAULT_PRODUCT_STATE } from './product-data/defaultState';
export type { UseProductDataProps, ProductDataResult } from './product-data/types';
